import {
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import PersonIcon from '@mui/icons-material/Person'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import React, { useEffect, useState } from 'react'
import { Modal } from '../../../components/_EOS/Modal'
import { ModalReturnType } from '../../../hooks/useModal'
import { IPickingRoute } from '../../../interfaces/PickingRoute/PickingRoute'
import { useApi } from '../../../hooks/useApi'
import { UserPickers } from '../../../interfaces/User/User'
import { Status } from '../../../hooks/useFetchState'
import {
  getPickingAvailableUsers,
  patchResetPickingRoute,
  postReassignUserPicker
} from '../api'

interface PickingRouteUserReassignProps {
  modalState: ModalReturnType<IPickingRoute>
  handleShowSnackbar: (msg: string | undefined, status: boolean) => void
  fetchDataGrid: () => void
}

export const PickingRouteUserReassign: React.FC<
  PickingRouteUserReassignProps
> = ({ modalState, handleShowSnackbar, fetchDataGrid }) => {
  const { state, callApi } = useApi<UserPickers[]>()

  const {
    state: reassignPickingUserState,
    callApi: reassignPickingUserCallApi
  } = useApi()

  const { state: resetPickingRoute, callApi: patchResetPickingRouteApi } =
    useApi()

  const [selectedUserPicker, setSelectedUserPicker] = useState<UserPickers>()

  const requestGetPickingAvailableUsers = () => {
    if (modalState.extendedProps) {
      callApi(getPickingAvailableUsers(modalState.extendedProps?.zoneId))
    }
  }

  const requestPostReassignUserPicker = () => {
    reassignPickingUserCallApi(
      postReassignUserPicker(
        modalState.extendedProps?.pickingRouteId || 0,
        selectedUserPicker?.userId || 0
      )
    )
      .then(() => {
        handleShowSnackbar('Usuario reasignado correctamente', true)
        fetchDataGrid()
        modalState.closeModal()
      })
      .catch((err) =>
        handleShowSnackbar(`No fue posible reasigna usuario: ${err}`, false)
      )
  }

  const requestPatchResetPickingRoute = () => {
    if (modalState.extendedProps) {
      patchResetPickingRouteApi(
        patchResetPickingRoute(modalState.extendedProps?.pickingRouteId)
      )
        .then(() => {
          handleShowSnackbar('Ruta Liberada correctamente', true)
          fetchDataGrid()
          modalState.closeModal()
        })
        .catch((err) => handleShowSnackbar(`${err}`, false))
    }
  }

  useEffect(() => {
    if (modalState.isOpen && modalState.extendedProps) {
      requestGetPickingAvailableUsers()
    }
  }, [modalState.isOpen, modalState.extendedProps])

  const isLoading =
    reassignPickingUserState.status === Status.Loading ||
    resetPickingRoute.status === Status.Loading

  return (
    <Modal
      onClose={modalState.closeModal}
      open={modalState.isOpen}
      title='Usuarios pickeadores'
      cancelButton={{ title: 'Cerrar', action: modalState.closeModal }}
    >
      <Grid container direction='column' spacing={2}>
        <Grid size={12}>
          <Typography variant='caption' style={{ fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
            Picker
          </Typography>
          <TextField
            select
            fullWidth
            variant='outlined'
            value={selectedUserPicker?.userId ?? ''}
            onChange={(e) => {
              const found = (state.data || []).find(
                (x) => x.userId === Number(e.target.value)
              )
              setSelectedUserPicker(found)
            }}
            disabled={state.status === Status.Loading || isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {state.status === Status.Loading ? (
                    <CircularProgress size='1.1rem' />
                  ) : (
                    <PersonIcon color='action' />
                  )}
                </InputAdornment>
              )
            }}
          >
            <MenuItem value=''>
              <em>Seleccionar usuario...</em>
            </MenuItem>
            {(state.data || []).map((item) => (
              <MenuItem key={item.userId} value={item.userId}>
                {item.userName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={12}>
          <Button
            variant='contained'
            color='primary'
            fullWidth
            onClick={() => {
              if (modalState.extendedProps) {
                requestPostReassignUserPicker()
              }
            }}
            startIcon={
              reassignPickingUserState.status === Status.Loading ? (
                <CircularProgress size='0.9rem' />
              ) : (
                <SwapHorizIcon />
              )
            }
            disabled={isLoading || !selectedUserPicker}
          >
            Reasignar Picker
          </Button>
        </Grid>

        <Grid size={12}>
          <Button
            variant='outlined'
            color='primary'
            fullWidth
            onClick={() => {
              if (modalState.extendedProps) {
                requestPatchResetPickingRoute()
              }
            }}
            startIcon={
              resetPickingRoute.status === Status.Loading ? (
                <CircularProgress size='0.9rem' />
              ) : (
                <LockOpenIcon />
              )
            }
            disabled={isLoading}
          >
            Liberar Ruta
          </Button>
        </Grid>

        {state.data?.length === 0 && (
          <Grid size={12}>
            <Typography variant='body2' style={{ color: 'red' }}>
              No hay usuarios pickeadores disponibles
            </Typography>
          </Grid>
        )}
      </Grid>
    </Modal>
  )
}
